import {
    Calendar,
    Link2,
    Users,
    Package,
    DollarSign,
    Bell,
    Building2,
    BarChart3,
    Megaphone,
    MessageCircle,
    Shield
} from 'lucide-react';

/**
 * FeaturesSection Component - Premium Dark Design v2
 *
 * Upgraded with new features: WhatsApp Campaigns, Bidirectional Confirmation,
 * Smart Notifications. Highlight card for star feature.
 */
function FeaturesSection() {
    const features = [
        // Row 1 — Core (emerald)
        {
            icon: Calendar,
            title: 'Agenda Inteligente',
            description: 'Calendario interactivo con anti-overbooking automático. Vista diaria, semanal y por equipo con filtros avanzados.',
            color: 'emerald',
            stat: null,
            badge: null,
            highlight: false
        },
        {
            icon: Link2,
            title: 'Reservas Públicas 24/7',
            description: 'Link compartible para que tus clientes reserven desde Instagram, WhatsApp o tu sitio web. Completamente personalizable.',
            color: 'teal',
            stat: null,
            badge: 'NUEVO',
            highlight: false
        },
        {
            icon: Users,
            title: 'CRM de Clientes',
            description: 'Base de datos completa con historial de visitas, notas de fórmulas, búsqueda instantánea y tipos de clientes (VIP, mayorista).',
            color: 'emerald',
            stat: null,
            badge: null,
            highlight: false
        },
        // Row 2 — Power (violet — highlight card)
        {
            icon: Megaphone,
            title: '📢 Campañas WhatsApp',
            description: 'Envía mensajes masivos para promociones del Día de la Madre, Semana Santa... ¡Con protección anti-ban automática y retrasos inteligentes!',
            color: 'violet',
            stat: 'anti-ban ✓',
            badge: 'NUEVO 🔥',
            highlight: true   // ← StarCard
        },
        {
            icon: MessageCircle,
            title: 'WhatsApp Bidireccional',
            description: 'Tus clientes confirman o cancelan su cita con un clic desde WhatsApp. Menos trabajo para ti y menos ausencias en tu salón.',
            color: 'violet',
            stat: '40% menos ausencias',
            badge: 'NUEVO',
            highlight: false
        },
        {
            icon: DollarSign,
            title: 'Control Financiero',
            description: 'Dashboard de ingresos, comisiones de estilistas, rentabilidad por producto y métricas clave en tiempo real.',
            color: 'violet',
            stat: null,
            badge: null,
            highlight: false
        },
        // Row 3 — Infrastructure (teal)
        {
            icon: Package,
            title: 'Inventario Multi-Ubicación',
            description: 'Stock en tiempo real para salón, bodega o casa. Alertas de stock bajo, movimientos y gestión de proveedores.',
            color: 'teal',
            stat: null,
            badge: null,
            highlight: false
        },
        {
            icon: Bell,
            title: 'Notificaciones Inteligentes',
            description: 'Push notifications en el celular aunque la app esté cerrada. Recordatorios automáticos 24h antes de la cita.',
            color: 'teal',
            stat: '10 min setup',
            badge: null,
            highlight: false
        },
        {
            icon: Shield,
            title: 'Seguridad & Multi-Salón',
            description: 'Aislamiento total de datos por salón. Roles (Dueño, Manager, Estilista). Gestiona múltiples locales desde una cuenta.',
            color: 'emerald',
            stat: null,
            badge: null,
            highlight: false
        },
        {
            icon: BarChart3,
            title: 'Reportes y Analytics',
            description: 'Dashboard en tiempo real con métricas clave. Identifica tendencias y toma decisiones basadas en datos reales.',
            color: 'teal',
            stat: null,
            badge: null,
            highlight: false
        },
        {
            icon: Building2,
            title: 'Multi-Salón',
            description: '¿Tienes más de un local? Gestiona todos tus salones desde una sola cuenta con un clic.',
            color: 'emerald',
            badge: 'PRO',
            stat: null,
            highlight: false
        },
    ];

    const iconColorMap: Record<string, string> = {
        emerald: 'from-emerald-500 to-teal-500',
        teal: 'from-teal-500 to-cyan-500',
        violet: 'from-violet-500 to-purple-500',
    };

    return (
        <section id="features" className="features-section">
            <div className="section-container">
                {/* Section Header */}
                <div className="section-header">
                    <span className="section-badge">TODO INCLUIDO • BETA GRATIS</span>
                    <h2 className="section-title">
                        Lo que necesitas para
                        <span className="gradient-text"> hacer crecer tu salón</span>
                    </h2>
                    <p className="section-description">
                        Herramientas profesionales para salones que quieren modernizarse. 
                        Durante nuestra fase Beta, <strong>todas las funciones son gratis e ilimitadas.</strong>
                    </p>
                </div>

                {/* Features Grid */}
                <div className="features-grid">
                    {features.map((feature, index) => {
                        const IconComponent = feature.icon;
                        const iconGrad = iconColorMap[feature.color] || iconColorMap.emerald;

                        if (feature.highlight) {
                            // ✨ Star card — wider with gradient border
                            return (
                                <div
                                    key={index}
                                    className="feature-card feature-highlight"
                                    style={{ animationDelay: `${index * 0.05}s` }}
                                >
                                    {feature.badge && (
                                        <span className="feature-badge badge-new">{feature.badge}</span>
                                    )}
                                    {/* Icon */}
                                    <div className={`feature-icon-gradient bg-gradient-to-br ${iconGrad}`}>
                                        <IconComponent size={26} strokeWidth={2} className="text-white" />
                                    </div>
                                    <h3 className="feature-title">{feature.title}</h3>
                                    <p className="feature-description">{feature.description}</p>
                                    {feature.stat && (
                                        <div className="feature-stat">
                                            <span className="feature-stat-dot" />
                                            {feature.stat}
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        return (
                            <div
                                key={index}
                                className={`feature-card feature-${feature.color}`}
                                style={{ animationDelay: `${index * 0.05}s` }}
                            >
                                {feature.badge && (
                                    <span className={`feature-badge ${feature.badge === 'NUEVO' || feature.badge?.startsWith('NUEVO') ? 'badge-new' : 'badge-pro'}`}>
                                        {feature.badge}
                                    </span>
                                )}
                                <div className={`feature-icon-gradient bg-gradient-to-br ${iconGrad}`}>
                                    <IconComponent size={24} strokeWidth={2} className="text-white" />
                                </div>
                                <h3 className="feature-title">{feature.title}</h3>
                                <p className="feature-description">{feature.description}</p>
                                {feature.stat && (
                                    <div className="feature-stat">
                                        <span className="feature-stat-dot" />
                                        {feature.stat}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export default FeaturesSection;
